import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";

import { getNavIconComponentForPathname } from "./pageHeaderNavIcon";

/**
 * Reusable page header component with gradient background
 * Based on the Dashboard header design
 *
 * @param {object} props
 * @param {React.ReactNode} [props.icon] — If set, overrides auto icon from route; use `null` to hide icon.
 */
const PageHeader = ({ title, subtitle, icon: iconProp }) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const AutoIcon = getNavIconComponentForPathname(pathname);
  const showIcon =
    iconProp !== undefined ? iconProp : AutoIcon ? <AutoIcon sx={{ fontSize: 40, opacity: 0.95 }} /> : null;

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: "white",
        py: { xs: 2, sm: 3 },
        flexShrink: 0, // Prevent compression in flex layout
        minWidth: 0,
      }}
    >
      {/* Match PageContent horizontal padding — do not use Container maxWidth="xl" or the
          header centers on wide viewports while page body stays left-aligned, leaving a large gap. */}
      <Box sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          {showIcon ? (
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "inherit",
                "& .MuiSvgIcon-root": { color: "inherit" },
              }}
              aria-hidden
            >
              {showIcon}
            </Box>
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontSize: { xs: "1.35rem", sm: "2rem" },
                lineHeight: { xs: 1.35, sm: 1.2 },
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                mb: subtitle ? undefined : 0,
              }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography
                variant="subtitle1"
                sx={{
                  opacity: 0.9,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  lineHeight: 1.4,
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default PageHeader;